import React, { useState, useCallback, useMemo } from 'react';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { CloudinaryImage } from 'taboola-ultimate-ui';
import { FORM_MODES } from 'config/formModes';
import { ThumbnailEditBanner } from 'modules/campaigns/modules/creative-editor/components/ThumbnailEditor/Banner';
import { PermissionsGuard } from 'modules/taboola-common-frontend-modules/authentication';
import { permissionsShape } from 'modules/taboola-common-frontend-modules/authentication/components/PermissionsGuard';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import useFormFieldValue from 'modules/taboola-common-frontend-modules/formData/hooks/useFormFieldValue';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import styles from './cloudinaryImageWithEditBanner.module.scss';

const classNameBuilder = classnames.bind(styles);

const CloudinaryImageWithEditBanner = ({ imageSrc, width, height, editPermissions, openCropModal }) => {
    const [isHovering, setIsHovering] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    const startHovering = useCallback(() => setIsHovering(true), [setIsHovering]);
    const stopHovering = useCallback(() => setIsHovering(false), [setIsHovering]);
    const setImageLoaded = useCallback(() => setIsLoaded(true), [setIsLoaded]);
    const { value: cropped } = useFormFieldValue({ field: 'creativeCrop' });
    const isCropped = useMemo(
        () => cropped?.cropData?.length > 0 && cropped?.cropData?.some(crop => crop.area),
        [cropped]
    );
    const { mode } = useFormDataContext();
    const isBulkEdit = mode === FORM_MODES.BULK_EDIT;

    const shouldShowBanner = isLoaded && isHovering && !isBulkEdit;

    const onClickEditCropped = useCallback(
        () => openCropModal({ src: decodeURIComponent(imageSrc) }),
        [openCropModal, imageSrc]
    );

    return (
        <div className={classNameBuilder('image-container')} onMouseMove={startHovering} onMouseLeave={stopHovering}>
            <CloudinaryImage
                src={imageSrc}
                width={width}
                height={height}
                sharpen={false}
                crop="fill"
                onLoad={setImageLoaded}
            />
            <>
                {isCropped && (
                    <div className={styles['cropped']}>
                        <FormattedMessage
                            id="video.creative.creator.media.upload.file.cropped"
                            defaultMessage="Cropped"
                        />
                    </div>
                )}
                <PermissionsGuard permissions={editPermissions}>
                    <ThumbnailEditBanner
                        className={classNameBuilder('banner', { hidden: !shouldShowBanner })}
                        onClick={onClickEditCropped}
                        text={<FormattedMessage id="crop.image.banner.label" defaultMessage="Edit" />}
                        dataMetrics="Cropping"
                    />
                </PermissionsGuard>
            </>
        </div>
    );
};

CloudinaryImageWithEditBanner.propTypes = {
    imageSrc: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    editPermissions: permissionsShape,
};

export default CloudinaryImageWithEditBanner;
